/**
 * A caixa da equipe: dúvidas, sugestões e problemas que chegaram.
 *
 * ## A ordem é ao contrário do resto da plataforma
 *
 * Toda outra lista aqui mostra o mais recente primeiro. Esta mostra **as
 * abertas antes, e dentro delas as mais antigas** — quem espera há mais tempo
 * é quem deve ser respondido antes. Ordenar por recência faria a mensagem de
 * ontem descer para sempre a cada nova que chegasse.
 *
 * ## Responder e encerrar deixaram de ser o mesmo gesto
 *
 * Eram: um campo de resposta que, salvo em branco, encerrava. Isso destruiu
 * uma resposta em produção — a mesma escrita que mudava o estado também
 * reescrevia o texto, e o texto virava nulo. Agora responder ACRESCENTA uma
 * fala à conversa, e encerrar não escreve conteúdo nenhum.
 *
 * ## O estado diz de quem é a bola
 *
 * "Aberta" não dizia nada: aberta por quem, esperando o quê. Os três estados
 * aqui são lidos do ponto de vista de quem atende — quem precisa agir agora.
 * Do outro lado, na tela de quem escreveu, o mesmo estado é dito ao contrário.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, SessionError, goToSignIn } from '../api.js';
import { relativeTime, waitingTime, exactTime } from '../time.js';
import { Icon } from './Icon.jsx';
import { notify } from './Toast.jsx';
import { Thread } from './FeedbackDialog.jsx';

const KINDS = {
  QUESTION: { label: 'Dúvida', icon: 'inbox', tone: 'info' },
  SUGGESTION: { label: 'Sugestão', icon: 'sparkles', tone: 'gold' },
  PROBLEM: { label: 'Problema', icon: 'x-circle', tone: 'danger' },
};

/** Do ponto de vista de quem atende. Ver o cabeçalho. */
const STATUS = {
  OPEN: {
    label: 'Esperando você',
    cls: 'pending',
    next: 'A última palavra é de quem escreveu. Responda abaixo — ou encerre, se não pede resposta.',
  },
  ANSWERED: {
    label: 'Respondida',
    cls: 'ok',
    next: 'A equipe já respondeu. Se a pessoa escrever de volta, a conversa volta para a fila.',
  },
  CLOSED: {
    label: 'Encerrada',
    cls: 'neutral',
    next: 'Fora da fila. Uma resposta da pessoa reabre a conversa sozinha.',
  },
};

/** As abas usam o mesmo vocabulário da pastilha — senão a tela discorda de si. */
const TABS = [
  { id: 'OPEN', label: 'Esperando você' },
  { id: 'ANSWERED', label: 'Respondidas' },
  { id: 'CLOSED', label: 'Encerradas' },
  { id: '', label: 'Todas' },
];

export function AdminFeedback({ initialId = null }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('OPEN');
  const [abertaId, setAbertaId] = useState(initialId);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setList((await api.admin.feedback()).feedback);
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      setError(err.message);
    } finally {
      setLoading(false);
    }
    return undefined;
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Veio do sino apontando para uma conversa. O filtro sai do caminho: a
  // mensagem pode estar em qualquer estado, e cair na aba errada mostraria uma
  // lista sem ela — que é o mesmo que não ter levado a lugar nenhum.
  useEffect(() => {
    if (!initialId) return;
    setAbertaId(initialId);
    setFiltro('');
  }, [initialId]);

  const visible = useMemo(
    () => (list ?? []).filter((f) => !filtro || f.status === filtro),
    [list, filtro],
  );

  const aberta = useMemo(() => (list ?? []).find((f) => f.id === abertaId), [list, abertaId]);
  const abertas = (list ?? []).filter((f) => f.status === 'OPEN').length;

  function onChanged(item) {
    setList((l) => l.map((f) => (f.id === item.id ? item : f)));
  }

  return (
    <>
      <div className="stats-bar">
        <div className="stat">
          <div className="stat-label">Esperando você</div>
          <div className="stat-value">{list ? abertas : '—'}</div>
        </div>
        <div className="stat">
          <div className="stat-label">Recebidas ao todo</div>
          <div className="stat-value">{list ? list.length : '—'}</div>
        </div>
      </div>

      <div className="list-panel">
        <div className="tabs-row">
          <div className="tabs" role="tablist" aria-label="Filtrar mensagens">
            {TABS.map((o) => (
              <button
                key={o.id || 'all'}
                type="button"
                role="tab"
                aria-selected={filtro === o.id}
                className={`tab ${filtro === o.id ? 'is-active' : ''}`}
                onClick={() => setFiltro(o.id)}
              >
                {o.label}
                {o.id === 'OPEN' && abertas > 0 && <span className="tab-badge">{abertas}</span>}
              </button>
            ))}
          </div>
        </div>

        {loading && <p className="state-message">Carregando…</p>}

        {error && (
          <div className="state-message error" role="alert">
            <p>{error}</p>
            <button type="button" onClick={load}>
              Tentar de novo
            </button>
          </div>
        )}

        {!loading && !error && visible.length === 0 && (
          <p className="state-message">
            {filtro === 'OPEN'
              ? 'Nada esperando resposta. A fila está limpa.'
              : 'Nenhuma mensagem com esse filtro.'}
          </p>
        )}

        {!loading && !error && visible.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th scope="col">Mensagem</th>
                  <th scope="col">De</th>
                  <th scope="col">Última fala</th>
                  <th scope="col">Situação</th>
                  <th scope="col" className="col-action">
                    <span className="visually-hidden">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((f) => {
                  const k = KINDS[f.kind] ?? KINDS.QUESTION;
                  const s = STATUS[f.status] ?? { label: f.status, cls: 'neutral' };
                  const quando = f.lastMessageAt ?? f.createdAt;
                  const quantas = f.messages?.length ?? 1;
                  return (
                    <tr key={f.id}>
                      <th scope="row" className="cell-person">
                        <Icon name={k.icon} tone={k.tone} />
                        <span className="person-text">
                          <span className="person-name">{f.subject}</span>
                          <span className="person-upn">
                            {k.label} · {quantas} {quantas === 1 ? 'mensagem' : 'mensagens'}
                          </span>
                        </span>
                      </th>

                      <td>
                        <span className="cell-main">{f.userName ?? '—'}</span>
                        <span className="cell-sub">{f.userUpn}</span>
                      </td>

                      <td>
                        <span className="cell-main" title={exactTime(quando)}>
                          {relativeTime(quando)}
                        </span>
                        {f.status === 'OPEN' && (
                          <span className="cell-sub">esperando {waitingTime(quando)}</span>
                        )}
                      </td>

                      <td>
                        <span className={`status-pill ${s.cls}`}>{s.label}</span>
                      </td>

                      <td className="col-action">
                        <button
                          type="button"
                          className="button ghost row-action"
                          onClick={() => setAbertaId(f.id)}
                        >
                          {f.status === 'OPEN' ? 'Responder' : 'Abrir'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="panel-ft">
          Quem escreveu recebe a resposta pelo sino e pode escrever de volta no mesmo assunto — a
          conversa inteira fica guardada. Encerrar tira da fila sem apagar nada, e uma resposta da
          pessoa reabre.
        </div>
      </div>

      {aberta && (
        <AnswerPanel item={aberta} onClose={() => setAbertaId(null)} onChanged={onChanged} />
      )}
    </>
  );
}

function AnswerPanel({ item, onClose, onChanged }) {
  const [mudando, setMudando] = useState(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const k = KINDS[item.kind] ?? KINDS.QUESTION;
  const encerrada = item.status === 'CLOSED';

  async function trocarEstado() {
    setMudando(true);
    try {
      const r = await api.admin.setFeedbackStatus(item.id, encerrada ? 'OPEN' : 'CLOSED');
      onChanged(r.feedback);
      notify(encerrada ? 'Conversa reaberta.' : 'Conversa encerrada.');
    } catch (err) {
      if (err instanceof SessionError) return goToSignIn();
      notify(err.message);
    } finally {
      setMudando(false);
    }
    return undefined;
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="item-panel editor-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="resposta-titulo"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="panel-header editor-head">
          <h2 id="resposta-titulo" className="panel-title">
            {item.subject}
          </h2>
          <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <dl className="facts thread-facts">
          <div className="fact-row">
            <dt>De</dt>
            <dd>{item.userName ?? item.userUpn}</dd>
          </div>
          <div className="fact-row">
            <dt>Tipo</dt>
            <dd>{k.label}</dd>
          </div>
          {item.page && (
            <div className="fact-row">
              <dt>Escreveu da tela</dt>
              <dd>
                <code>{item.page}</code>
              </dd>
            </div>
          )}
        </dl>

        <Thread
          item={item}
          onReplied={onChanged}
          statusCopy={STATUS[item.status]}
          onReply={async (texto) => (await api.admin.answerFeedback(item.id, texto)).feedback}
        >
          <button type="button" className="button ghost" onClick={trocarEstado} disabled={mudando}>
            {encerrada ? 'Reabrir' : 'Encerrar sem responder'}
          </button>
        </Thread>
      </div>
    </div>
  );
}

export default AdminFeedback;
